import { Card, Form, Input, message, Select, Typography } from 'antd'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import './blogEditor.scss'
import ContentToolbar from './ContentToolbar'
import OrganizationInput from 'components/global/inputs/OrganizationInput/OrganizationInput'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { communityStore } from 'state/event/community'
import { useGetOrgOrganizers, useGetOrganizationDetails } from 'api/Organizations'
import { OrgOrganizerParams } from 'types/organization'
import EventManager from 'pages/Events/Views/CreateEvent/Screens/GeneralDetails/InputComps/EventManager'
import { useDispatch, useSelector } from 'react-redux'
import { resizeImageToJPEG } from 'helper/utility'
import { useFileUploadRequest, useFileUploadToServer } from 'api/FileUpload'

const { Option } = Select
const { Title } = Typography

export const PostDetailsCard = ({ form, content, setContent, BlogDetails, onAuthorsChange, currentOrganization, setCurrentOrganization, isEditMode }: any) => {
  const { communityData, setCommunityData } = communityStore(store => ({
    communityData: store.communityData,
    setCommunityData: store.setCommunityData,
  }))
  const [selectedOrganization, setSelectedOrganization] = useState<
    any | undefined
  >(currentOrganization || (communityData && communityData.organization) || undefined)
  const [params, setParams] = useState<OrgOrganizerParams>({
    status: 'approved',
  })
  const [selectedOrganizer, setSelectedOrganizer] = useState<string[]>([])
  const [isDefaultOrganizer, setIsDefaultOrganizer] = useState<boolean>(true)
  const [defaultOrganizer, setDefaultOrganizer] = useState(null)
  const { data: orgOrganizers, refetch: orgOrganizersRefetch } = useGetOrgOrganizers(params, selectedOrganization?.id)
  const { refetch: refetchOrganizationDetails } = useGetOrganizationDetails(selectedOrganization?.id)
  const quillRef = useRef<any>(null)
  const hiddenFileInput = useRef<HTMLInputElement>(null)
  const { fileData, loading } = useSelector((state: any) => state.fileUpload);
  const dispatch = useDispatch();
  const { mutateAsync: requestUpload } = useFileUploadRequest();
  const { mutateAsync: uploadFile } = useFileUploadToServer();

  // Set default organizer from organization details
  useEffect(() => {
    if (orgOrganizers?.length > 0 && isDefaultOrganizer && !BlogDetails?.authors?.length) {
      const ownerOrganizer = orgOrganizers.find(
        (item: any) => item.accessLevel === 'owner'
      )

      if (ownerOrganizer) {
        setDefaultOrganizer(ownerOrganizer.organizer)
        setSelectedOrganizer([ownerOrganizer.organizerId])
      }
    }
  }, [orgOrganizers, isDefaultOrganizer, BlogDetails?.authors])


  useEffect(() => {
    if (!orgOrganizers) return

    const selectedAuthors = orgOrganizers
      .filter(org => selectedOrganizer.includes(org.organizerId))
      .map(org => ({
        id: org.organizerId,
        firstName: org.organizer.firstName,
        lastName: org.organizer.lastName || '',
        avatar: org.organizer.photo?.post
      }))

    onAuthorsChange?.(selectedAuthors)

  }, [selectedOrganizer, orgOrganizers])


  useEffect(() => {
    if (!BlogDetails) return

    if (BlogDetails.organization) {
      setSelectedOrganization(BlogDetails.organization)
    }

    setIsDefaultOrganizer(false)

    form.setFieldsValue({
      title: BlogDetails.title,
      organizationId: BlogDetails.organizationId,
    })

    setContent(BlogDetails.content || '')
  }, [BlogDetails])


  useEffect(() => {
    if (!isEditMode && !BlogDetails) {
      setSelectedOrganization(undefined);
      setSelectedOrganizer([]);
      setIsDefaultOrganizer(true);
      setDefaultOrganizer(null);
      setCurrentOrganization?.(null);
    }
  }, [isEditMode, BlogDetails]);

  useEffect(() => {
    if (
      BlogDetails?.authors?.length &&
      orgOrganizers?.length
    ) {
      const authorIds = BlogDetails.authors.map(a => a.id)

      setSelectedOrganizer(authorIds)
      setIsDefaultOrganizer(false)

      form.setFieldValue('authorIds', authorIds)
    }
  }, [BlogDetails?.authors, orgOrganizers, form])


  useEffect(() => {
    form.setFieldValue('authorIds', selectedOrganizer);
  }, [selectedOrganizer, form]);

  useEffect(() => {
    if (!selectedOrganization?.id) return

    orgOrganizersRefetch()
    refetchOrganizationDetails()

    // Don't return early - let the effect run when orgOrganizers updates
    if (!orgOrganizers?.length) return

    // If we're in edit mode and have BlogDetails with authors, prioritize those
    if (isEditMode && BlogDetails?.authors?.length) {
      const blogAuthorIds = BlogDetails.authors.map(a => a.id)
      const validBlogAuthorIds = blogAuthorIds.filter(authorId =>
        orgOrganizers.some(org => org.organizerId === authorId)
      )
      
      if (validBlogAuthorIds.length > 0) {
        setSelectedOrganizer(validBlogAuthorIds)
        form.setFieldValue('authorIds', validBlogAuthorIds)
        return
      }
    }

    // Filter authors to only include those that belong to the current organization
    const validAuthorIds = selectedOrganizer?.filter(authorId =>
      orgOrganizers.some(org => org.organizerId === authorId)
    ) || []

    if (validAuthorIds.length > 0) {
      // Keep only valid authors from the current organization
      setSelectedOrganizer(validAuthorIds)
      form.setFieldValue('authorIds', validAuthorIds)
    } else {
      // If no valid authors, default to the owner of the current organization
      const owner = orgOrganizers.find(o => o.accessLevel === 'owner')
      if (owner) {
        setSelectedOrganizer([owner.organizerId])
        form.setFieldValue('authorIds', [owner.organizerId])
      } else {
        setSelectedOrganizer([])
        form.setFieldValue('authorIds', [])
      }
    }
  }, [selectedOrganization?.id, orgOrganizers, form, isEditMode, BlogDetails?.authors])


  // Sync selectedOrganization with parent component
  useEffect(() => {
    if (selectedOrganization !== currentOrganization) {
      setCurrentOrganization?.(selectedOrganization)
    }
  }, [selectedOrganization, currentOrganization, setCurrentOrganization])

  // Sync selectedOrganization with form field
  useEffect(() => {
    if (selectedOrganization?.id) {
      form.setFieldValue('organizationId', selectedOrganization.id)
    } else {
      form.setFieldValue('organizationId', undefined)
    }
  }, [selectedOrganization, form])


 const quillImageHandler = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    const range = quill?.getSelection();
    (window as any).savedCursorIndex = range ? range.index : quill?.getLength();
    hiddenFileInput.current?.click();
  }, []);


  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e?.target) {
      console.error('Event target is undefined')
      return
    }
    const file = e.target.files?.[0]
    if (!file) return
    const quill = quillRef.current?.getEditor()
    const index = (window as any).savedCursorIndex ?? quill?.getLength() ?? 0
    const hideLoading = message.loading('Uploading image...', 0)

    try {
      // Step A: Process image
      const jpegBlob = await resizeImageToJPEG(file)
      const fileObj = new FormData()
      fileObj.append('file', new File([jpegBlob], `blog_${Date.now()}.jpeg`, { type: 'image/jpeg' }))

      // Step B: Get ID from server (Mutation 1)
      const requestData = await requestUpload({
        payload: fileObj,
        newFile: { title: 'Editor Img', sourceType: 'other' }
      })

      // Step C: Upload actual file (Mutation 2)
      const uploadResponse = await uploadFile({
        id: requestData._id,
        payload: fileObj
      })

      // Step D: Insert into Editor
      const imageUrl = uploadResponse?.post || uploadResponse?.data?.post
      if (imageUrl) {
        quill.insertEmbed(index, 'image', imageUrl)
        quill.setSelection(index + 1)
      }
    } catch (err) {
      console.error(err)
      message.error('Upload failed')
    } finally {
      hideLoading()
      e.target.value = '' // Clear input so same file can be uploaded twice
      delete (window as any).savedCursorIndex
    }
  }

  const linkHandler = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const range = quill.getSelection();
    if (range) {
      const template = '[add title](add-url)';
      quill.insertText(range.index, template);
      quill.setSelection(range.index + 1, 9);
    }
  }, [quillRef]);

  const modules = useMemo(() => ({
    toolbar: {
      container: '#content-toolbar',
      handlers: {
        image: quillImageHandler,
        link: linkHandler,
      }
    },
  }), [quillImageHandler, linkHandler]);
  return (
    <Card className="post-details-card">
      <Title level={4} className="card-title">
        Post Details
      </Title>
      <Form.Item
        name="title"
        label={<span className="required-input">Title</span>}
        rules={[{ required: true, message: 'Please enter a blog title' }]}
      >
        <Input placeholder="Enter blog title" />
      </Form.Item>

      <Form.Item
        name="organizationId"
        required
        rules={[
          { required: true, message: 'Please select an organization' }
        ]}
      >
        <OrganizationInput
          componentname='Blog'
          selectedOrganization={selectedOrganization}
          setSelectedOrganization={setSelectedOrganization}
        />
      </Form.Item>

      {selectedOrganization && orgOrganizers?.length > 0 && (
        <Form.Item
          required
          name="authorIds"
          rules={[
            { required: true, message: 'Please select an author' }
          ]}
        >
          <EventManager
            componentname='Blog'
            key={selectedOrganization?.id}
            selectedOrganizer={selectedOrganizer}
            setSelectedOrganizer={setSelectedOrganizer}
            orgOrganizers={orgOrganizers}
            defaultOrganizer={defaultOrganizer}
          />
        </Form.Item>
      )}

      <Form.Item
        required
        rules={[
          { required: true, message: 'Please enter blog content' },
          {
            validator: (_, value) => {
              if (!content || content.trim().length < 20) {
                return Promise.reject('Content must be at least 20 characters long')
              }
              return Promise.resolve()
            }
          }
        ]}
      >
        <input
          type="file"
          ref={hiddenFileInput}
          style={{ display: 'none' }}
          accept="image/*"
          onChange={handleFileChange}
        />
        <label className="input-label required-input">Content</label>
        <div className="editor-container" style={{ height: '600px' }}>
          <ContentToolbar />
          <ReactQuill
            ref={quillRef}
            value={content}
            onChange={setContent}
            placeholder="Write a blog post..."
            theme="snow"
            modules={modules}
            formats={[
              'header',
              'size',
              'bold',
              'italic',
              'underline',
              'list',
              'bullet',
              'link',
              'image',
            ]}
          />
        </div>
      </Form.Item>

    </Card>
  )
}
